import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Combobox: text input with an autocomplete dropdown of existing options.
 * User can type anything new, or click a suggestion. Kebab-case test IDs.
 */
export default function Combobox({
  value,
  onChange,
  options = [],
  placeholder,
  disabled,
  id,
  testid = "combobox",
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filter = (value || "").trim().toLowerCase();
  const filtered = options.filter((o) => !filter || o.toLowerCase().includes(filter));
  const showNew = filter && !options.some((o) => o.toLowerCase() === filter);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div className="relative">
        <Input
          id={id}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={`${testid}-input`}
          className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2 pr-9"
          autoComplete="off"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => !disabled && setOpen((v) => !v)}
          data-testid={`${testid}-toggle`}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-ink-muted hover:text-ink mt-1 disabled:opacity-30"
          disabled={disabled}
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (filtered.length > 0 || showNew) && (
        <div
          className="absolute left-0 right-0 top-full mt-1 bg-paper-50 border border-paper-200 rounded-sm shadow-lg z-20 max-h-60 overflow-y-auto"
          data-testid={`${testid}-dropdown`}
        >
          {filtered.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => pick(o)}
              data-testid={`${testid}-option-${o.toLowerCase().replace(/\s+/g, "-")}`}
              className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-paper-100 transition-colors"
            >
              {o}
            </button>
          ))}
          {showNew && (
            <button
              type="button"
              onClick={() => pick(value.trim())}
              data-testid={`${testid}-option-new`}
              className="w-full text-left px-3 py-2 text-sm text-forest hover:bg-paper-100 transition-colors border-t border-paper-200"
            >
              + Create "{value.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
